import { memo, useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";

interface EarlyStagePendingStepProps {
  onContinueAtStandardRate: () => Promise<void>;
}

function EarlyStagePendingStepInner({
  onContinueAtStandardRate,
}: EarlyStagePendingStepProps) {
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    setLoading(true);
    try {
      await onContinueAtStandardRate();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <header>
        <h4 className="mb-2 fw-semibold">Your Application Is Being Reviewed</h4>
        <p className="text-muted mb-0">
          We&apos;ve received your application. An admin will contact you
          regarding your application. You can stay on this step while we review
          it — there is nothing else you need to do right now.
        </p>
      </header>

      <Card className="border-0 shadow-sm bg-primary-subtle">
        <Card.Body className="p-4">
          <div className="d-flex align-items-start gap-3 mb-4">
            <span
              className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: "3rem", height: "3rem", fontSize: "1.35rem" }}
              aria-hidden
            >
              <i className="bi bi-hourglass-split" />
            </span>
            <div className="flex-grow-1 min-w-0">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <h5 className="fw-semibold mb-0">Review in progress</h5>
                <span className="badge rounded-pill fw-semibold bg-warning-subtle text-warning-emphasis border border-warning-subtle">
                  Pending
                </span>
              </div>
              <p className="text-muted small mb-0">
                Our team typically reviews applications within 1–2 business
                days. We will contact you once a decision has been made.
              </p>
            </div>
          </div>

          <div className="d-flex flex-column gap-3 p-3 px-4 rounded-2 bg-white bg-opacity-75">
            <div className="d-flex align-items-center gap-2 small text-success fw-medium">
              <i className="bi bi-check-circle-fill" aria-hidden />
              <span>Application submitted</span>
            </div>
            <div className="d-flex align-items-center gap-2 small text-primary fw-semibold">
              <i className="bi bi-circle-fill" style={{ fontSize: "0.55rem" }} aria-hidden />
              <span>Admin review</span>
            </div>
            <div className="d-flex align-items-center gap-2 small text-muted">
              <i className="bi bi-circle small" aria-hidden />
              <span>Decision &amp; next steps</span>
            </div>
          </div>

          <div className="d-flex align-items-start gap-2 p-3 rounded-2 small text-secondary mt-4 mb-0 bg-white bg-opacity-75">
            <i className="bi bi-info-circle text-primary flex-shrink-0 mt-1" aria-hidden />
            <span>
              You may close this page and sign in again later. After approval you
              can choose £2/month or £10/month, then complete payment.
            </span>
          </div>
        </Card.Body>
      </Card>

      <Card className="bg-light shadow-none border border-dashed border-secondary-subtle">
        <Card.Body className="p-4">
          <p className="small text-uppercase fw-semibold text-muted mb-2">
            Optional — don&apos;t want to wait?
          </p>
          <h6 className="fw-semibold mb-2">
            Get immediate access at £10/month
          </h6>
          <p className="text-muted small mb-3">
            If you want to gain access to your account right away, you may
            continue without the Young Professional rate. Your application stays
            with the admin team and choosing £10/month does not cancel it.
          </p>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={loading}
            onClick={() => void handleContinue()}
          >
            {loading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  className="me-2"
                  aria-hidden
                />
                Preparing…
              </>
            ) : (
              "Continue at £10/month"
            )}
          </Button>
        </Card.Body>
      </Card>
    </div>
  );
}

export default memo(EarlyStagePendingStepInner);
